// src/components/dfs/StickySummaryBar.tsx
import React, { useState, useEffect } from 'react';
import Spinner from '@/components/common/Spinner';

interface SummaryData {
    totalSalary: number;
    remainingSalary: number;
    playersCount: number;
    isValid: boolean;
}

interface StickySummaryBarProps {
    summary: SummaryData;
    cap: number;
    onSubmit: () => void;
    isSubmitting: boolean;
    isEditing: boolean;
    validationErrors: string[];
    isAnyPlayerLockedInLineup: boolean;
    onClearLineup: () => void;
    hasClearablePlayers: boolean;
    hasMobileBottomNav: boolean;
    submitError?: string | null;
}

const StickySummaryBar: React.FC<StickySummaryBarProps> = ({
    summary,
    cap,
    onSubmit,
    isSubmitting,
    isEditing,
    validationErrors,
    isAnyPlayerLockedInLineup,
    onClearLineup,
    hasClearablePlayers,
    hasMobileBottomNav,
    submitError,
}) => {
    const [isConfirmingClear, setIsConfirmingClear] = useState(false);

    // Auto-cancel the confirm state after 4 seconds of inaction
    useEffect(() => {
        if (!isConfirmingClear) return;
        const id = setTimeout(() => setIsConfirmingClear(false), 4000);
        return () => clearTimeout(id);
    }, [isConfirmingClear]);

    const salaryUsagePercent = cap > 0 ? (summary.totalSalary / cap) * 100 : 0;
    const isOverCap = summary.totalSalary > cap;

    // The plain "Lineup must have N players" message is redundant on mobile -
    // the PLAYERS x/8 counter and the disabled "Not Enough Players" button
    // already convey it. Only surface real problems (over cap, wrong position
    // counts) in the red box. The full validationErrors array still drives the
    // submit-disable logic below, so nothing can be submitted incomplete.
    const shownValidationErrors = validationErrors.filter(
        (e) => !e.startsWith('Lineup must have')
    );

    const isSubmitDisabled =
        isSubmitting ||
        (!submitError && (!summary.isValid || validationErrors.length > 0)) ||
        (!isEditing && isAnyPlayerLockedInLineup);

    const getSubmitButtonText = () => {
        if (isSubmitting) return isEditing ? 'Updating...' : 'Submitting...';
        if (!isEditing && isAnyPlayerLockedInLineup) return 'Players Locked';
        if (submitError) return 'Retry Submit';
        if (summary.totalSalary > cap) return 'Over Salary Cap';
        if (summary.playersCount < 8) return 'Not Enough Players';
        return isEditing ? 'Update Lineup' : 'Submit Lineup';
    };

    return (
        <div className={`fixed left-0 right-0 bg-primary border-t-2 border-gray-700 shadow-top_heavy p-3 z-30 lg:relative lg:bottom-auto lg:border-t-0 lg:p-0 lg:shadow-none lg:bg-transparent ${
            hasMobileBottomNav
                ? 'bottom-[var(--app-mobile-bottom-nav-height)]'
                : 'bottom-0 pb-[max(0.75rem,env(safe-area-inset-bottom))]'
        }`}>
            <div className="max-w-md mx-auto lg:max-w-none">
                {shownValidationErrors.length > 0 && (
                    <div className="mb-3 bg-red-900/30 border border-red-700/50 text-secondary p-2 rounded-md text-center lg:hidden">
                        {shownValidationErrors.map((err, i) => (
                            <p key={i} className="font-semibold text-xs">{err}</p>
                        ))}
                    </div>
                )}

                <div className="flex justify-between items-center mb-2 lg:hidden">
                    <div className="flex items-center space-x-2 text-xs">
                        <div className="lg:hidden">
                            <span className="font-semibold text-gray-400">PLAYERS:</span>
                            <span className={`ml-1 font-bold ${summary.playersCount === 8 ? 'text-green-400' : 'text-accent-yellow'}`}>
                                {summary.playersCount}/8
                            </span>
                        </div>
                        {hasClearablePlayers && !isConfirmingClear && (
                            <button
                                onClick={() => setIsConfirmingClear(true)}
                                className="text-xs text-gray-400 hover:text-secondary active:text-secondary px-1 py-0.5"
                                title="Remove all non-locked players"
                            >
                                (Clear)
                            </button>
                        )}
                        {hasClearablePlayers && isConfirmingClear && (
                            <span className="flex items-center gap-1.5">
                                <button
                                    onClick={() => { onClearLineup(); setIsConfirmingClear(false); }}
                                    className="text-xs font-bold text-secondary active:underline px-1 py-0.5"
                                >
                                    Clear
                                </button>
                                <span className="text-badge text-gray-600">|</span>
                                <button
                                    onClick={() => setIsConfirmingClear(false)}
                                    className="text-xs text-gray-400 hover:text-white active:text-white px-1 py-0.5"
                                >
                                    Cancel
                                </button>
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-right lg:hidden">
                        <span className="font-semibold text-gray-400">SALARY REMAINING:</span>
                        <span className={`ml-1 font-bold ${isOverCap ? 'text-secondary' : 'text-accent-yellow'}`}>
                            ${summary.remainingSalary.toLocaleString()}
                        </span>
                    </div>
                </div>

                <div className="w-full bg-primary rounded-full h-1.5 border border-gray-600 overflow-hidden mb-3 lg:hidden">
                    <div
                        className={`h-full rounded-full transition-all duration-300 ease-out ${
                            isOverCap ? 'bg-secondary' : 'bg-accent-yellow'
                        }`}
                        style={{ width: `${Math.min(salaryUsagePercent, 100)}%` }}
                    />
                </div>

                {submitError && (
                    <p className="text-secondary text-xs text-center mb-2 font-semibold">
                        {submitError}
                    </p>
                )}

                <button
                    onClick={onSubmit}
                    disabled={isSubmitDisabled}
                    className={`w-full py-3 lg:py-2 px-4 rounded-lg font-heading font-bold text-lg lg:text-base transition-colors duration-200 shadow-lg ${
                        isSubmitDisabled
                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            : 'bg-accent-yellow hover:bg-accent-yellow/90 active:bg-accent-yellow/90 text-primary transform hover:scale-[1.02] active:scale-[0.98]'
                    }`}
                >
                    {isSubmitting && <Spinner size="sm" color="text-white" className="mr-2 inline-block"/>}
                    {getSubmitButtonText()}
                </button>
            </div>
        </div>
    );
};

export default StickySummaryBar;
